import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Building2, Plus, AlertTriangle, RefreshCw, CheckCircle2, X } from 'lucide-react';
import { useActivities } from '../hooks/useActivities';
import { DashboardStats, ServicesGrid, ClientGrid, ContactModal, ExpiringServicesAlert } from '../components/Dashboard';
import { PlanModal } from '../components/Services';
import { CONTACT_REQUIRED_SERVICES } from '../config/services';
import { startCheckout, readCheckoutReturn } from '../services/checkout';

export default function Dashboard() {
  const {
    currentActivity,
    activities,
    loading: activitiesLoading,
    switchActivity,
    getServicesDashboard,
    getSubscriptionStats,
    activateTrial,
    activateSubscription,
    accessService
  } = useActivities();

  const [services, setServices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [contactService, setContactService] = useState(null);
  // Esito del ritorno da Stripe Checkout (?checkout=success|cancel), letto una volta sola
  const [checkoutReturn, setCheckoutReturn] = useState(() => readCheckoutReturn());

  // Vista corrente: "client" (per cliente) o "service" (panoramica, solo agenzia)
  const [currentView, setCurrentView] = useState('client');

  // Cache servizi per tutti i clienti (per ClientGrid)
  const [allServicesMap, setAllServicesMap] = useState({});

  // Euristica tipo account: ≥2 attività = agenzia
  // TODO: sostituire con campo esplicito da Supabase (es. user.is_agency) quando disponibile
  const isAgency = activities.length >= 2;

  // Carica servizi per attività corrente
  const loadDashboardData = useCallback(async () => {
    if (!currentActivity?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [servicesResult, statsResult] = await Promise.all([
        getServicesDashboard(),
        getSubscriptionStats()
      ]);

      if (servicesResult.success) {
        setServices(servicesResult.data || []);
        setAllServicesMap(prev => ({ ...prev, [currentActivity.id]: servicesResult.data }));
      } else {
        setError(servicesResult.error);
      }

      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentActivity?.id, getServicesDashboard]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Dopo un pagamento il webhook Stripe attiva il piano in qualche secondo: ricarico i servizi
  // un paio di volte per farlo comparire senza che il cliente debba aggiornare la pagina.
  useEffect(() => {
    if (checkoutReturn?.status !== 'success') return;
    const timers = [4000, 12000].map((ms) => setTimeout(loadDashboardData, ms));
    return () => timers.forEach(clearTimeout);
  }, [checkoutReturn, loadDashboardData]);

  // Quando cambia l'attività selezionata, mostra la vista "Per cliente"
  useEffect(() => {
    if (currentActivity?.id) {
      setCurrentView('client');
    }
  }, [currentActivity?.id]);

  // Carica servizi per tutte le attività (per vista "Per servizio")
  useEffect(() => {
    if (!isAgency) return;

    const loadAllServices = async () => {
      for (const activity of activities) {
        if (allServicesMap[activity.id]) continue;
        try {
          const result = await getServicesDashboard(activity.id);
          if (result.success) {
            setAllServicesMap(prev => ({ ...prev, [activity.id]: result.data }));
          }
        } catch {
          // silently skip
        }
      }
    };

    loadAllServices();
  }, [isAgency, activities, getServicesDashboard]);

  // Separa servizi "miei" da "disponibili" (come layout precedente)
  const { myServices, availableServices } = useMemo(() => {
    const filtered = services.filter(s => s.service.isActive !== false);

    const mine = filtered
      .filter(s => s.subscription)
      .sort((a, b) => {
        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;
        return (a.service.sortOrder || 0) - (b.service.sortOrder || 0);
      });

    const available = filtered
      .filter(s => !s.subscription)
      .sort((a, b) => (a.service.sortOrder || 0) - (b.service.sortOrder || 0));

    return { myServices: mine, availableServices: available };
  }, [services]);

  // Handlers
  const handleAccessService = async (serviceCode) => {
    const result = await accessService(serviceCode);
    if (!result.success) {
      alert(result.error || 'Impossibile aprire il servizio. Riprova.');
    }
  };

  const handleActivateTrial = async (serviceCode) => {
    const result = await activateTrial(serviceCode);
    if (result.success) {
      loadDashboardData();
    } else {
      alert(result.error || 'Errore nell\'attivazione del trial');
    }
  };

  const handleChoosePlan = (serviceCode) => {
    const service = services.find(s => s.service.code === serviceCode);
    if (service) setSelectedService(service);
  };

  // Rinnovo o attivazione a pagamento: apre la scelta del piano (mensile/annuale), che poi
  // porta al checkout Stripe nativo. I servizi senza checkout nativo vanno al link statico o
  // a WhatsApp come prima.
  const handleRenew = (serviceCode) => {
    const serviceItem = services.find(s => s.service.code === serviceCode);
    if (serviceItem && ['review', 'page', 'menu'].includes(serviceCode)) {
      setSelectedService(serviceItem);
      return;
    }
    startCheckout({
      serviceCode,
      activityId: currentActivity?.id,
      billingCycle: serviceItem?.subscription?.billingCycle || 'monthly',
      fallbackUrl: serviceItem?.service?.paymentUrl,
      serviceName: serviceItem?.service?.name,
      activityName: currentActivity?.name || '',
    });
  };

  const handleConfigureService = async (serviceCode) => {
    const result = await accessService(serviceCode);
    if (!result.success) {
      alert(result.error || 'Errore nell\'accesso al servizio');
    }
  };

  const handleRequestInfo = (serviceCode) => {
    const apiService = services.find(s => s.service.code === serviceCode);
    if (apiService) {
      setContactService(apiService.service);
    } else if (CONTACT_REQUIRED_SERVICES[serviceCode]) {
      setContactService(CONTACT_REQUIRED_SERVICES[serviceCode]);
    }
  };

  const handleSelectActivityFromGrid = (activityId) => {
    const activity = activities.find(a => a.id === activityId);
    if (activity) {
      switchActivity(activity);
      setCurrentView('client');
    }
  };

  // Nessuna attività
  if (!activitiesLoading && !currentActivity) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Nessuna attività
          </h2>
          <p className="text-gray-500 mb-6">
            {activities.length === 0
              ? 'Crea la tua prima attività per iniziare ad utilizzare i servizi doID.'
              : 'Seleziona un\'attività dal menu per visualizzare i servizi.'}
          </p>
          {activities.length === 0 && (
            <Link to="/activities/new" className="btn-primary inline-flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              Crea Attività
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Loading
  if (activitiesLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Errore nel caricamento</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button onClick={loadDashboardData} className="btn-primary inline-flex items-center">
            <RefreshCw className="w-4 h-4 mr-2" />
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Topbar */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {currentView === 'client'
              ? (currentActivity?.name || 'Dashboard')
              : 'Panoramica clienti'
            }
          </h1>
          <p className="text-gray-500 mt-1">
            {currentView === 'client'
              ? 'Gestisci i servizi doID per questo cliente'
              : `${activities.length} clienti nella tua agenzia`
            }
          </p>
        </div>

        {/* Toggle vista (solo agenzia) */}
        {isAgency && (
          <div className="flex bg-gray-100 rounded-lg p-1 w-full lg:w-auto">
            <button
              onClick={() => setCurrentView('client')}
              className={`
                flex-1 lg:flex-none px-4 py-2 text-sm font-medium rounded-md transition-all
                ${currentView === 'client'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                }
              `}
            >
              Per cliente
            </button>
            <button
              onClick={() => setCurrentView('service')}
              className={`
                flex-1 lg:flex-none px-4 py-2 text-sm font-medium rounded-md transition-all
                ${currentView === 'service'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                }
              `}
            >
              Per servizio
            </button>
          </div>
        )}
      </div>

      {/* Ritorno da Stripe Checkout */}
      {checkoutReturn && (
        <div className={`mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 ${
          checkoutReturn.status === 'success'
            ? 'border-green-200 bg-green-50 text-green-800'
            : 'border-gray-200 bg-gray-50 text-gray-700'
        }`}>
          {checkoutReturn.status === 'success' && <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />}
          <p className="text-sm flex-1">
            {checkoutReturn.status === 'success'
              ? 'Pagamento ricevuto, grazie! Il piano si attiva da solo entro qualche secondo; se non lo vedi, aggiorna la pagina tra un minuto.'
              : 'Pagamento annullato: non ti abbiamo addebitato niente.'}
          </p>
          <button onClick={() => setCheckoutReturn(null)} className="opacity-60 hover:opacity-100" aria-label="Chiudi">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Content */}
      {currentView === 'service' && isAgency ? (
        <ClientGrid
          activities={activities}
          currentActivityId={currentActivity?.id}
          onSelectActivity={handleSelectActivityFromGrid}
          servicesMap={allServicesMap}
        />
      ) : (
        <>
          {/* Stats */}
          <DashboardStats stats={stats} />

          {/* Servizi scaduti / in scadenza */}
          <ExpiringServicesAlert
            services={services}
            activityName={currentActivity?.name || ''}
            activityId={currentActivity?.id || null}
            allServicesMap={isAgency ? allServicesMap : null}
            activities={activities}
          />

          {/* I tuoi servizi attivi */}
          {myServices.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">I tuoi servizi</h2>
              <ServicesGrid
                services={myServices}
                onAccessService={handleAccessService}
                onActivateTrialService={handleActivateTrial}
                onChoosePlanService={handleChoosePlan}
                onRenewService={handleRenew}
                onConfigureService={handleConfigureService}
                onRequestInfoService={handleRequestInfo}
              />
            </div>
          )}

          {/* Scopri gli altri servizi */}
          {availableServices.length > 0 && (
            <div className="mb-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Scopri gli altri servizi</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Potenzia la tua attività con i servizi digitali doID
                </p>
              </div>
              <ServicesGrid
                services={availableServices}
                onAccessService={handleAccessService}
                onActivateTrialService={handleActivateTrial}
                onChoosePlanService={handleChoosePlan}
                onRenewService={handleRenew}
                onConfigureService={handleConfigureService}
                onRequestInfoService={handleRequestInfo}
              />
            </div>
          )}
        </>
      )}

      {/* Plan Modal */}
      {selectedService && (
        <PlanModal
          service={selectedService.service}
          hasPhysicalProduct={selectedService.hasPhysicalProduct || false}
          onClose={() => setSelectedService(null)}
          onActivate={async (planId, billingCycle, plan) => {
            const price = billingCycle === 'yearly' ? plan?.priceYearly : plan?.priceMonthly;
            if (price > 0) {
              // Piano a pagamento: checkout Stripe, il webhook attiva il piano al pagamento
              await startCheckout({
                serviceCode: selectedService.service.code,
                activityId: currentActivity?.id,
                billingCycle,
                fallbackUrl: selectedService.service.paymentUrl,
                serviceName: selectedService.service.name,
                activityName: currentActivity?.name || '',
              });
              return;
            }
            // PlanModal passa l'id del piano, il backend cerca il codice ('free', 'pro'):
            // con l'id rispondeva "Piano non trovato"
            const result = await activateSubscription(
              selectedService.service.code,
              plan?.code || planId,
              billingCycle
            );
            if (result.success) {
              setSelectedService(null);
              loadDashboardData();
            } else {
              alert(result.error || 'Errore nell\'attivazione dell\'abbonamento');
            }
          }}
        />
      )}

      {/* Contact Modal */}
      {contactService && (
        <ContactModal
          service={contactService}
          onClose={() => setContactService(null)}
        />
      )}
    </div>
  );
}
